import type { GroupedTasks, UserDailySummary } from '../../../lib/dailyApi';
import { dailyTaskRow, TaskListRow } from '../../tasks/TaskListRow';
import { useI18n } from '../../../contexts/I18nContext';
import { QuickStatusIcon, useQuickStatusColor } from './MyDailyStatusSection';
import type { MemberCheckIns } from './BusinessSummarySection';

interface Props {
  summary: UserDailySummary;
  /** Today's check-ins for this member (badges + "Raportat N/M"). */
  checkIns?: MemberCheckIns;
}

// Order + labels for the due-date timeline buckets (CLE-58).
const BUCKETS: { key: keyof GroupedTasks; label: string }[] = [
  { key: 'overdue', label: 'Întârziate' },
  { key: 'today', label: 'Astăzi' },
  { key: 'tomorrow', label: 'Mâine' },
  { key: 'next', label: 'Următoarele 2-3 zile' },
];

// Initials from a display name, e.g. "Andrei Pata" → "AP".
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * One team member's card in the business-wide overview (CLE-58): avatar /
 * initials, name + total task count, and their active tasks grouped on
 * the due-date timeline (Overdue / Today / Tomorrow / Next 2-3 Days). The
 * Overdue bucket is highlighted in red as an urgency indicator.
 */
export function UserTimelineCard({ summary, checkIns }: Props) {
  const { t } = useI18n();
  const colorOf = useQuickStatusColor();
  const { user, grouped, total } = summary;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-3">
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="h-9 w-9 rounded-full object-cover" />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
            {initials(user.name)}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate font-semibold text-gray-900">{user.name}</p>
          <p className="text-xs text-gray-500">
            {total} {total === 1 ? 'task' : 'task-uri'} în total
          </p>
        </div>
        {checkIns && checkIns.total > 0 && (
          <span
            className={`ml-auto shrink-0 rounded-full border px-2 py-px text-[11px] font-medium ${
              checkIns.notReported
                ? 'border-amber-300 text-amber-700'
                : 'border-emerald-300 text-emerald-700'
            }`}
          >
            {checkIns.notReported
              ? t('dailyStatus.card.notReported')
              : t('dailyStatus.card.reported', { done: checkIns.checkedIn, total: checkIns.total })}
          </span>
        )}
      </div>

      {total === 0 ? (
        <p className="text-sm text-gray-400">Fără task-uri</p>
      ) : (
        <div className="space-y-3">
          {BUCKETS.map(({ key, label }) => {
            const tasks = grouped[key];
            if (tasks.length === 0) return null;
            const overdue = key === 'overdue';
            return (
              <div key={key}>
                <p
                  className={`text-xs font-bold uppercase tracking-wider ${
                    overdue ? 'text-red-600' : 'text-gray-500'
                  }`}
                >
                  {overdue && <span aria-hidden>⚠ </span>}
                  {label} ({tasks.length})
                </p>
                <ul
                  className={`mt-1 overflow-hidden rounded-md border ${
                    // Overdue rows keep a red left edge as the urgency cue; the
                    // "N zile" due slot in the row carries the rest (CLE-62 kept
                    // the tint theme-aware, and the row's own colours already are).
                    overdue ? 'border-red-300 border-l-2' : 'border-gray-200'
                  }`}
                >
                  {tasks.map((task) => {
                    const status = checkIns?.byTask[task.id];
                    const quick = status ? { value: status, color: colorOf(status) } : null;
                    return (
                      <TaskListRow
                        key={task.id}
                        task={dailyTaskRow(task)}
                        tone={overdue ? 'overdue' : 'plain'}
                        newTab
                        trailing={
                          quick ? (
                            <span
                              title={t(`dailyStatus.status.${quick.value}`)}
                              className="mr-2 inline-flex h-6 shrink-0 items-center rounded-md border border-gray-300 px-1.5 text-[11px] font-medium"
                              style={{ color: quick.color }}
                            >
                              <QuickStatusIcon status={quick.value} color={quick.color} size={12} />
                              <span className="ml-1 hidden xl:inline">
                                {t(`dailyStatus.status.${quick.value}`)}
                              </span>
                            </span>
                          ) : undefined
                        }
                      />
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
